import { Box, CircularProgress, Divider, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { mapCompanyToSupplierCardData } from '../company/common/supplier-card.mappers';
import { SupplierCardData } from '../company/common/SupplierCard';
import { PageWrapper } from '../common/components/PageWrapper';
import ProductRecommendations from '../products/recommendations/ProductRecommendations';
import { mapProductFragmentToProductCardData } from '../products/common/product-card.mappers';
import { ProductCardData } from '../products/common/product-card.types';
import { useWishlistCompaniesByIdsQuery } from '../queries/__generated__/wishlistCompaniesByIds.generated';
import { useProductsByIdsQuery } from '../queries/__generated__/productsByIds.generated';
import { currentContextAtom } from '../state';
import { storeWishlistVisit } from './wishlist.utils';
import WishlistEmptyState from './WishlistEmptyState';
import WishlistProductList from './WishlistProductList';
import WishlistSupplierList from './WishlistSupplierList';
import WishlistTabs from './WishlistTabs';
import WishlistViewToggle from './WishlistViewToggle';
import {
  wishlistActiveTabAtom,
  wishlistItemsAtom,
  wishlistLoadingAtom,
  wishlistSupplierAtom,
} from './state';

export default function WishlistPage(): React.JSX.Element {
  const { t } = useTranslation(['wishlist']);
  const { roleAssignmentId } = useAtomValue(currentContextAtom);
  const activeTab = useAtomValue(wishlistActiveTabAtom);
  const wishlistItems = useAtomValue(wishlistItemsAtom);
  const wishlistSuppliers = useAtomValue(wishlistSupplierAtom);
  const wishlistLoading = useAtomValue(wishlistLoadingAtom);

  useEffect(() => {
    if (roleAssignmentId) {
      storeWishlistVisit(roleAssignmentId);
    }
  }, [roleAssignmentId]);

  const { data: productsData, loading: productsLoading } = useProductsByIdsQuery({
    skip: wishlistLoading || !wishlistItems.length,
    variables: {
      where: {
        _or: wishlistItems.map(({ supplier_id, sku }) => ({
          company_id: { _eq: supplier_id },
          sku: { _eq: sku },
        })),
      },
    },
  });

  const { data: suppliersData, loading: suppliersLoading } = useWishlistCompaniesByIdsQuery({
    skip: wishlistLoading || !wishlistSuppliers.length,
    variables: {
      where: {
        id: { _in: wishlistSuppliers.map(({ supplier_id }) => supplier_id) },
      },
    },
  });

  const products = useMemo((): ProductCardData[] => {
    const productsByKey = new Map(
      (productsData?.product || []).map((product) => [
        `${product.company_id}:${product.sku}`,
        mapProductFragmentToProductCardData(product),
      ]),
    );

    return wishlistItems
      .map(({ supplier_id, sku }) => productsByKey.get(`${supplier_id}:${sku}`))
      .filter((product) => !!product);
  }, [productsData, wishlistItems]);

  const suppliers = useMemo((): SupplierCardData[] => {
    const suppliersById = new Map(
      (suppliersData?.company || []).map((company) => [
        company.id,
        mapCompanyToSupplierCardData(company),
      ]),
    );

    return wishlistSuppliers
      .map(({ supplier_id }) => suppliersById.get(supplier_id))
      .filter((supplier) => !!supplier);
  }, [suppliersData, wishlistSuppliers]);

  const loading = wishlistLoading || productsLoading || suppliersLoading;


  const isProductTab = activeTab === 'product';
  const isEmpty = !loading && ((wishlistItems.length === 0 && isProductTab) || (wishlistSuppliers.length === 0 && !isProductTab));

  return (
    <PageWrapper sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Typography variant="headlineMedium">
          {t('wishlist:page_title')}
        </Typography>
        {!loading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              alignItems: 'center',
            }}
          >
            <WishlistTabs />
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                height: '32px',
                my: 'auto',
                borderColor: 'outlineVariant.main',
                display: {
                  xs: 'none',
                  lg: 'flex',
                },
              }}
            />
            <WishlistViewToggle />
          </Box>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 12 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && isEmpty && <WishlistEmptyState variant={isProductTab ? 'product' : 'supplier'} />}

        {!loading && !isEmpty && (
          <>
            {isProductTab && (
              <WishlistProductList products={products} />
            )}
            {!isProductTab && (
              <WishlistSupplierList suppliers={suppliers} />
            )}
            <ProductRecommendations />
          </>
        )}
      </Box>
    </PageWrapper>
  );
}
